import { useState, useEffect, useRef, useCallback } from "react";
import { useSearch, useLocation } from "wouter";

import { api, ApiError } from "../../lib/api";
import { RefreshError } from "../../components/PullToRefresh";
import { captureProfileGuard, profileKey } from "../../lib/profileSession";
import { useSavedMutationPending } from "../../lib/saved";
import { useAuth } from "../../lib/auth";
import { MOCK_DATA_ENABLED } from "../../lib/dataMode";

import { analyticsFeatureForMarketplaceTab, emitAnalyticsFeatureAction } from "../../lib/analytics";
import { useI18n } from "../../lib/i18n";
import { TABS, normalizeTab, mapService, MOCK, emptyFilters, LOAD_MORE_ANIMATION_MIN_MS, hasRating, hasRatingFilter, fetchDiscoveryPage, filtersFromUrl, ApplicationStatusesState, DIRECTORY_GROUPS, TAB_PRESENTATION, SURFACE_META } from "./model";
export function useMarketplaceController({ surface }: { surface?: string } = {}) {

  const { lang, t } = useI18n();
  const tabLabel = (value: string) => {
    const key: Record<string, string> = { Jobseekers: "discovery.jobseekers", Jobs: "discovery.jobs", Services: "discovery.services", Companies: "discovery.companies", "Project Bidding": "discovery.projects", RFQs: "discovery.rfqs", Marketplace: "discovery.marketplace", "Materials, Products & Equipment": "discovery.marketplaceTitle" };
    return key[value] ? t(key[value]) : value;
  };
  const groupLabel = (group: (typeof DIRECTORY_GROUPS)[number]) => group.key === "providers"
    ? t("discovery.providers")
    : group.key === "opportunities" ? t("discovery.opportunities") : t("discovery.marketplace");
  const search = useSearch();
  const [, navigate] = useLocation();
  const params0 = new URLSearchParams(search);
  // v3.9.49 — when locked to a surface, that tab is fixed and the tab bar is hidden.
  const locked = surface && TABS.includes(surface) ? surface : null;
  const meta = locked ? SURFACE_META[locked] : null;
  const initial = locked || normalizeTab(params0.get("tab")) || "Services";
  const startTab = TABS.includes(initial) ? initial : "Services";
  const [tab, setTab] = useState(startTab);
  const urlTabRef = useRef(startTab);
  // Seed filters from the URL so a search coming from the home page (or a shared
  // link) shows results immediately.
  const [f, setF] = useState({
    ...emptyFilters,
    ...filtersFromUrl(params0, startTab),
  });
  // view.tab and view.items always belong together, so a card never receives a
  // mismatched record while a tab switch is mid-flight.
  const [view, setView] = useState<{ tab: string; items: any[]; nextCursor: string | null; total: number }>({ tab: startTab, items: MOCK_DATA_ENABLED ? MOCK[startTab] : [], nextCursor: null, total: MOCK_DATA_ENABLED ? MOCK[startTab].length : 0 });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const loadMoreInFlightRef = useRef(false);
  const loadMoreArmedRef = useRef(true);
  const requestGenerationRef = useRef(0);
 const [fallback, setFallback] = useState(false);
  const [loadError, setLoadError] = useState(false);
 const [retryKey, setRetryKey] = useState(0);
  // Start every directory in grid view and keep each tab's selected layout.
  const [layoutByTab, setLayoutByTab] = useState<Record<string, "grid" | "list">>({});
  const layout = layoutByTab[tab] ?? "grid";
  const setLayout = (nextLayout: "grid" | "list") => {
    setLayoutByTab((current) => ({ ...current, [tab]: nextLayout }));
  };
  const [cityOptions, setCityOptions] = useState<string[]>([]);
  useEffect(() => {
    let current = true;
    api.geoCityOptions().then(({ items }) => {
      if (current) setCityOptions(items.map(item => item.name));
    }).catch(() => { /* Free location entry remains usable without suggestions. */ });
    return () => { current = false; };
  }, []);
  const [showFilters, setShowFilters] = useState(false); // mobile filter bottom sheet
  const { user } = useAuth();
  const savedMutationPending = useSavedMutationPending();
  const [refreshing, setRefreshing] = useState(false);
  const refreshInFlightRef = useRef(false);
  const refreshIdentity = JSON.stringify([tab, f, profileKey(user), search]);
  const currentRefreshIdentity = useRef(refreshIdentity);
  currentRefreshIdentity.current = refreshIdentity;
  const isWorker = Boolean(user?.capabilities?.includes("find_work"));
  const lastAnalyticsTab = useRef<string | null>(null);
  const [applicationStatuses, setApplicationStatuses] = useState<Record<string, string>>({});
  const [applicationStatusesState, setApplicationStatusesState] = useState<ApplicationStatusesState>("loading");
  const applicationGeneration = useRef(0);

  useEffect(() => {
    refreshInFlightRef.current = false;
    setRefreshing(false);
  }, [refreshIdentity]);

  useEffect(() => {
    if (locked || !user || lastAnalyticsTab.current === tab) return;
    lastAnalyticsTab.current = tab;
    if (user.userType === "admin") return;
    const feature = analyticsFeatureForMarketplaceTab(tab);
    if (!feature) return;
    void emitAnalyticsFeatureAction(feature);
  }, [locked, tab, user]);

  useEffect(() => {
    if (!isWorker || tab !== "Jobs") {
      setApplicationStatuses({});
      setApplicationStatusesState("ready");
      return;
    }
    let cancelled = false;
    const generation = ++applicationGeneration.current;
    setApplicationStatusesState("loading");
    api.myApplications()
      .then((applications) => {
        if (cancelled || generation !== applicationGeneration.current) return;
        setApplicationStatuses(Object.fromEntries(applications.map((application) => [application.jobId, application.status])));
        setApplicationStatusesState("ready");
      })
      .catch(() => {
        if (!cancelled && generation === applicationGeneration.current) {
          setApplicationStatuses({});
          setApplicationStatusesState("error");
        }
      });
    return () => { cancelled = true; };
  }, [isWorker, tab]);

  const set = (patch: Partial<typeof emptyFilters>) => setF((prev) => ({ ...prev, ...patch }));
  const resetFilters = () => setF({ ...emptyFilters });

  // Keep the view in sync with the URL query (header nav + home search).
  // "Explore" points at /marketplace with no ?tab=, so a missing tab resets to
  // the default rather than leaving the previous tab stuck (fixes Explore no-op).
  useEffect(() => {
    const p = new URLSearchParams(search);
    const requestedTab = normalizeTab(p.get("tab"));
    const nextTab = locked || (requestedTab && TABS.includes(requestedTab) ? requestedTab : "Services");
    const sectionChanged = urlTabRef.current !== nextTab;
    urlTabRef.current = nextTab;
    setTab(nextTab);
    // [marketplace.filters-and-pagination](../../../../docs/product/marketplace.md#marketplace-filters-and-pagination): navigation clears obsolete section filters, preserves typed
    // keywords/location, then applies supported filters from an explicit shared URL.
    const patch = filtersFromUrl(p, nextTab);
    if (sectionChanged || Object.keys(patch).length) setF((prev) => ({
      ...(sectionChanged ? { ...emptyFilters, q: prev.q, city: prev.city } : prev), ...patch,
    }));
  }, [search, locked]);

  // debounced server-side fetch for the active tab + filters
  useEffect(() => {
    let alive = true;
    const generation = ++requestGenerationRef.current;
    loadMoreArmedRef.current = true;
    setLoading(true);
    loadMoreInFlightRef.current = false;
    setLoadingMore(false);
    const h = setTimeout(() => {
      fetchDiscoveryPage(tab, f)
        .then((page) => {
          if (!alive || generation !== requestGenerationRef.current || refreshIdentity !== currentRefreshIdentity.current) return;
          // Vite's SPA fallback can return HTML with a 200 when the local API is
          // absent. Treat a malformed page as a failed fetch so explicit mock
          // mode remains usable and production shows the honest error state.
          if (!page || !Array.isArray(page.items)) throw new Error("Invalid marketplace response");
          const list = page.items || [];
         setView({ tab, items: tab === "Services" ? list.map(mapService) : list, nextCursor: page.nextCursor || null, total: page.total ?? list.length });
         setFallback(false);
          setLoadError(false);
        })
        .catch(() => {
          if (!alive || generation !== requestGenerationRef.current || refreshIdentity !== currentRefreshIdentity.current) return;
         setView({ tab, items: MOCK_DATA_ENABLED ? MOCK[tab] : [], nextCursor: null, total: MOCK_DATA_ENABLED ? MOCK[tab].length : 0 });
         setFallback(MOCK_DATA_ENABLED);
          setLoadError(!MOCK_DATA_ENABLED);
        })
        .finally(() => {
          if (alive && generation === requestGenerationRef.current && refreshIdentity === currentRefreshIdentity.current) setLoading(false);
        });
    }, 300);
    return () => { alive = false; clearTimeout(h); };
  }, [tab, f.q, f.city, f.category, f.categories, f.itemTypes, f.profession, f.minRating, f.verified, f.sort, f.orgTypes, retryKey, refreshIdentity]);

  // Manual reads preserve the visible page until success and invalidate older
  // continuation/badge reads before they can overwrite the refreshed selection.
  async function refreshDiscovery(signal: AbortSignal) {
    const identity = refreshIdentity;
    const generation = ++requestGenerationRef.current;
    const badgeGeneration = ++applicationGeneration.current;
    const assertProfileCurrent = captureProfileGuard();
    const assertCurrent = () => {
      assertProfileCurrent();
      if (signal.aborted || identity !== currentRefreshIdentity.current || generation !== requestGenerationRef.current) {
        throw new DOMException("Refresh is no longer current.", "AbortError");
      }
    };
    const finish = () => {
      if (identity !== currentRefreshIdentity.current || generation !== requestGenerationRef.current) return;
      refreshInFlightRef.current = false;
      setRefreshing(false);
    };
    const invalidate = () => { finish(); if (generation === requestGenerationRef.current) requestGenerationRef.current++; };
    signal.addEventListener("abort", invalidate, { once: true });
    refreshInFlightRef.current = true;
    setRefreshing(true);
    loadMoreInFlightRef.current = false;
    loadMoreArmedRef.current = false;
    setLoadingMore(false);
    try {
      const [pageRead, badgeRead] = await Promise.allSettled([
        fetchDiscoveryPage(tab, f, undefined, signal),
        isWorker && tab === "Jobs" ? api.myApplications(signal) : Promise.resolve(null),
      ]);
      assertCurrent();
      if (pageRead.status === "rejected") throw pageRead.reason;
      const page = pageRead.value;
      if (!page || !Array.isArray(page.items)) throw new RefreshError(t("refresh.error"));
      setView({ tab, items: tab === "Services" ? page.items.map(mapService) : page.items,
        nextCursor: page.nextCursor || null, total: page.total ?? page.items.length });
      setFallback(false); setLoadError(false);
      if (isWorker && tab === "Jobs" && badgeGeneration === applicationGeneration.current) {
        if (badgeRead.status === "fulfilled" && Array.isArray(badgeRead.value)) {
          setApplicationStatuses(Object.fromEntries(badgeRead.value.map(application => [application.jobId, application.status])));
          setApplicationStatusesState("ready");
        } else {
          setApplicationStatuses({}); setApplicationStatusesState("error");
          if (badgeRead.status === "rejected" && badgeRead.reason instanceof ApiError && badgeRead.reason.status === 401) {
            throw new RefreshError(t("refresh.sessionExpired"));
          }
          throw new RefreshError(t("refresh.applicationsError"));
        }
      }
    } catch (failure: unknown) {
      if (!signal.aborted && identity === currentRefreshIdentity.current && generation === requestGenerationRef.current
        && failure instanceof ApiError && [401, 403, 404].includes(failure.status)) {
        setApplicationStatuses({}); setApplicationStatusesState("error");
        if (failure.status === 401) throw new RefreshError(t("refresh.sessionExpired"));
      }
      throw failure;
    } finally {
      signal.removeEventListener("abort", invalidate);
      finish();
    }
  }

  // v3.9.0 — load organization types for the Companies-tab filter (once).
  const [orgTypes, setOrgTypes] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    if (tab === "Companies" && orgTypes.length === 0) {
      api.orgTypes().then(setOrgTypes).catch(() => {});
    }
  }, [tab, orgTypes.length]);

  const items = view.items;
  const count = items.length;
  const activeGroup = DIRECTORY_GROUPS.find((group) => group.tabs.includes(tab)) ?? DIRECTORY_GROUPS[0];
  const presentation = TAB_PRESENTATION[tab] ?? { label: tab, intent: "Browse available listings." };
  const intentKeys: Record<string, string> = {
    Services: "discovery.servicesIntent", Jobseekers: "discovery.jobseekersIntent", Companies: "discovery.companiesIntent",
    Jobs: "discovery.jobsIntent", "Project Bidding": "discovery.projectsIntent", RFQs: "discovery.rfqsIntent", Marketplace: "discovery.marketplaceIntent",
  };
  const presentationIntent = intentKeys[tab] ? t(intentKeys[tab]) : presentation.intent;
  const visibleTotal = view.tab === tab ? view.total : 0;
  const sectionSelections = tab === "Marketplace" ? f.itemTypes : tab === "Companies" ? f.orgTypes : tab === "Jobseekers" ? [] : f.categories;
  const filtersActive = Boolean(f.q || f.city || f.category || sectionSelections.length || (tab === "Jobseekers" && f.profession) || (hasRatingFilter(tab) && f.minRating > 0) || (hasRating(tab) && f.verified) || f.sort !== "newest");
  // count of sheet filters active (keywords live in the search box, shown separately)
  const activeCount = sectionSelections.length + [f.city, f.category, tab === "Jobseekers" && f.profession, hasRatingFilter(tab) && f.minRating > 0, hasRating(tab) && f.verified].filter(Boolean).length;

  const selectTab = (nextTab: string) => {
    setF((previous) => ({ ...emptyFilters, q: previous.q, city: previous.city }));
    setTab(nextTab);
    navigate(`/marketplace?tab=${encodeURIComponent(nextTab)}`);
  };

  const loadMore = useCallback(async () => {
    const cursor = view.nextCursor;
    if (!cursor || view.tab !== tab || loadMoreInFlightRef.current || refreshInFlightRef.current) return;
    loadMoreInFlightRef.current = true;
    loadMoreArmedRef.current = false;
    setLoadingMore(true);
    const generation = requestGenerationRef.current;
    const animationMinimum = new Promise<void>((resolve) => window.setTimeout(resolve, LOAD_MORE_ANIMATION_MIN_MS));
    try {
      const page = await fetchDiscoveryPage(tab, f, cursor);
      await animationMinimum;
      // A filter change can reuse the same cursor. Never append a previous selection's page to the new results.
      if (generation !== requestGenerationRef.current || refreshIdentity !== currentRefreshIdentity.current) return;
      const nextItems = page.items || [];
      setView((prev) => prev.tab !== tab || prev.nextCursor !== cursor ? prev : ({
          ...prev,
          items: [...prev.items, ...(tab === "Services" ? nextItems.map(mapService) : nextItems)],
          nextCursor: page.nextCursor || null,
          total: page.total ?? prev.total,
        }));
    } catch {
      await animationMinimum;
      /* keep the already-loaded results visible */
    }
    finally {
      if (generation === requestGenerationRef.current && refreshIdentity === currentRefreshIdentity.current) {
        loadMoreInFlightRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [f, tab, view.nextCursor, view.tab, refreshIdentity]);

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !view.nextCursor || view.tab !== tab || fallback || loading || refreshing || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver((entries) => {
      const entry = entries.find((observedEntry) => observedEntry.target === sentinel);
      if (!entry) return;
      if (!entry.isIntersecting) {
        loadMoreArmedRef.current = true;
        return;
      }
      if (!loadMoreArmedRef.current || loadMoreInFlightRef.current) return;
      loadMoreArmedRef.current = false;
      void loadMore();
    }, { rootMargin: "0px" });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fallback, loadMore, loading, loadingMore, refreshing, tab, view.nextCursor, view.tab]);



return { lang, t, tabLabel, groupLabel, locked, meta, tab, f, view, loading, loadingMore, loadMoreSentinelRef, fallback, loadError, setRetryKey, layout, setLayout, cityOptions, showFilters, setShowFilters, isWorker, applicationStatuses, applicationStatusesState, set, resetFilters, orgTypes, items, count, activeGroup, intentKeys, presentationIntent, visibleTotal, sectionSelections, filtersActive, activeCount, selectTab, loadMore, refreshIdentity, savedMutationPending, refreshing, refreshDiscovery };
}
export type MarketplaceState = ReturnType<typeof useMarketplaceController>;
